import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { displayToast } from '@rsos/components/capstone/base/Toast';
import { useFlag } from '@rsos/flags';
import {
  useKeywordSets,
  useKeywordTiers,
} from '@rsos/reduxDataStore/src/features/keywords';
import { setSelectedKeywordMatch } from '../EventPane/tabs/TranscriptTab/transcriptionSlice';
import { KeywordNotification } from './KeywordNotification';
import { trackKeywordBannerClicked } from './KeywordNotificationTracking';
import type { KeywordDetectionMessage } from './keywordNotificationsSlice';
import {
  useKeywordHasNotified,
  keywordNotificationShown,
  keywordNotificationsSelector,
  keywordDetectionRecieved,
} from './keywordNotificationsSlice';

const THIRTY_SECONDS = 1000 * 30;

export function useIsCallerInQueue(): (callerId: string) => boolean {
  const callQueue = useSelector(
    (state: { emergencyCalls: { callQueue: Record<string, unknown> } }) =>
      state.emergencyCalls.callQueue,
  );
  return (callerId: string) => callerId in callQueue;
}

function useKeywordShouldNotify(): (keywordSetId: string) => boolean {
  const { keywordSets } = useKeywordSets();
  const { tierMap } = useKeywordTiers();
  if (!tierMap) {
    return () => false;
  }
  const notificationMap = Object.fromEntries(
    keywordSets.map(s => [s.id, tierMap[s.tier_id].notifications]),
  );
  return (keywordSetId: string) => notificationMap[keywordSetId] || false;
}

function useNavigateToCallTranscript() {
  const dispatch = useDispatch();
  return (callerId: string, keywordSetID: string): void => {
    dispatch({ type: 'emergencyCalls/setSelectedCallerID', payload: callerId });
    dispatch({ type: 'eventPane/setSelectedTab', payload: 'transcript' });
    dispatch(
      setSelectedKeywordMatch({
        callerId,
        match: { keywordSetID, matchIndex: 0 },
      }),
    );
  };
}

/**
 * This is a wrapper component that uses the @see useKeywordNotifications hook to allow inserting it in to the render tree within a provider.
 */
export const KeywordNotifications = () => {
  useKeywordNotifications();
  return <></>;
};

/**
 * Hook that monitors keyword matches and dispatches toast notifications
 * for matches that belong to tiers with notifications enabled.
 */
export function useKeywordNotifications(): void {
  useKeywordNotificationMessagesHook();
  const dispatch = useDispatch();
  const { keywordDetections } = useSelector(keywordNotificationsSelector);
  const shouldNotify = useKeywordShouldNotify();
  const hasNotified = useKeywordHasNotified();
  const navigateToCallTranscript = useNavigateToCallTranscript();
  const isCallerInQueue = useIsCallerInQueue();
  const filterKeywordsByQueue = useFlag('filter_keyword_alerts_by_queue');

  // get the matches that are configured to notify:
  const matchesToNotify = keywordDetections.filter(
    match =>
      shouldNotify(match.keywordSetId) &&
      !hasNotified(match) &&
      (!filterKeywordsByQueue || isCallerInQueue(match.callerId)),
  );

  useEffect(() => {
    for (const match of matchesToNotify) {
      // Dispatch to Redux store to track that we've shown this notification
      dispatch(keywordNotificationShown(match));

      // Display an actual toast notification to the user
      const onClickAction = () => {
        trackKeywordBannerClicked(match.keywordSetId, match.keywordSetName);
        navigateToCallTranscript(match.callerId, match.keywordSetId);
      };

      displayToast({
        message: <KeywordNotification match={match} />,
        options: {
          autoClose: THIRTY_SECONDS,
          onClick: onClickAction,
          closeButton: true,
        },
        className: 'Toastify__alert-default',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesToNotify]);
}

export function useKeywordNotificationMessagesHook() {
  const dispatch = useDispatch();

  const handleKeywordNotifications = useCallback(
    (message: Record<string, unknown>) => {
      if (message?.type === 'keyword_detection.v1') {
        const action = {
          keywordSetId: message.keyword_set_id,
          keywordSetName: message.keyword_set_name,
          callerId: message.caller_id,
        };

        dispatch(keywordDetectionRecieved(action as KeywordDetectionMessage));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const StreamWS = window?.__RSOS?.streamWS?.instance;
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleKeywordNotifications,
      });
      return () =>
        StreamWS.removeOnMessageCallback('handleKeywordNotifications');
    }
  }, [handleKeywordNotifications]);
}
